import { z } from 'zod'
import { accountSchema, accountStatusSchema } from './account'

/**
 * Marking an account inactive, and what is still running under it. Inactive is a label: it changes
 * nothing about access or sending by itself. Pausing what still runs is a separate decision the
 * person makes when warned (`pauseRunning`), and turning the account back on restarts nothing.
 */

/** A product under an account, named for the warning. */
export const accountProductRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  subaccountId: z.uuid(),
  subaccountName: z.string(),
})

/** A report or RTA. `legacyDelivery` means the old portal delivers it, so a pause here stops nothing. */
export const runningDeliverableSchema = accountProductRefSchema.extend({
  legacyDelivery: z.boolean(),
})

export const accountRunningResponseSchema = z.object({
  reports: z.array(runningDeliverableSchema),
  rtas: z.array(runningDeliverableSchema),
  apiEndpoints: z.array(accountProductRefSchema),
  /** Sends already scheduled on those reports. Pausing cancels them; left alone, they go out. */
  scheduledSends: z.number().int().nonnegative(),
  /** False when the caller lacks `manage-all:users`: endpoints are listed and left running. */
  canPauseApiEndpoints: z.boolean(),
})

export const setAccountStatusSchema = z.object({
  status: accountStatusSchema,
  /** Marking inactive only. Reactivating never touches a product. */
  pauseRunning: z.boolean().default(false),
})

export const productCountsSchema = z.object({
  reports: z.number().int().nonnegative(),
  rtas: z.number().int().nonnegative(),
  apiEndpoints: z.number().int().nonnegative(),
})

export const setAccountStatusResponseSchema = z.object({
  account: accountSchema,
  paused: productCountsSchema,
  leftRunning: productCountsSchema,
  /** On reactivation: what stays paused until someone turns it back on by hand. */
  stillPaused: productCountsSchema,
})

export type AccountRunningResponse = z.infer<typeof accountRunningResponseSchema>
export type SetAccountStatus = z.infer<typeof setAccountStatusSchema>
export type SetAccountStatusResponse = z.infer<typeof setAccountStatusResponseSchema>
export type ProductCounts = z.infer<typeof productCountsSchema>
